import { useState } from 'react';
import { DEFAULT_DRAWER_REAR_CLEARANCE, DRAWER_BOARD_LABEL, drawerBoardEdit, MAX_DRAWERS, setDrawerBoard, type AssemblyInstance, type DrawerBoard, type DrawerSet, type MaterialSlot } from '@scs/domain-engine';
import { IntInput, MmInput } from '../lib/ui';
import type { EditorStore } from './store';

type Exec = EditorStore['exec'];

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="prop-row">
      <label>{label}</label>
      {children}
    </div>
  );
}

const SLOT_LABEL: Record<MaterialSlot, string> = {
  'drawer-bottom': 'Dno ladice (kao leđa)',
  back: 'Leđa',
  carcass: 'Korpus',
  'drawer-box': 'Kutija ladice',
  front: 'Fronte',
  plinth: 'Sokla',
};

/** Drawer set ("Ladice"): count, slide / top / bottom clearance, box and bottom boards, bottom material. */
export function DrawerProperties({ inst, set, index, exec, disabled, onRemoved }: { inst: AssemblyInstance; set: DrawerSet; index: number | null; exec: Exec; disabled: boolean; onRemoved(): void }) {
  const lock = disabled || inst.locked;
  const edit = (label: string, fn: (s: DrawerSet) => void) =>
    exec(label, (d) => {
      const s = d.assemblies.find((a) => a.id === inst.id)?.drawerSets?.find((x) => x.id === set.id);
      if (s) fn(s);
    });
  /** Board edits go to the selected drawer, or to every drawer of the set. */
  const [allDrawers, setAllDrawers] = useState(index === null);
  const target = allDrawers || index === null ? ('all' as const) : index;
  const shown = index ?? 1;
  const boards: DrawerBoard[] = set.frontPanel === false ? ['pod', 'bl', 'bd', 'z'] : ['pod', 'bl', 'bd', 'z', 'p'];
  const editBoard = (b: DrawerBoard, change: { off?: boolean; dh?: number }, what: string) =>
    edit(`${what}: ${DRAWER_BOARD_LABEL[b].toLowerCase()}${target === 'all' ? ' (sve ladice)' : ` (ladica ${target})`}`, (s) => setDrawerBoard(s, target, b, change));
  return (
    <div className="prop-group drawer-group">
      <h3>
        ▥ {set.label}
        {index !== null && <span className="faint"> · ladica {index}</span>}
      </h3>
      <Row label="Naziv">
        <input
          className="input"
          style={{ height: 26 }}
          defaultValue={set.label}
          key={set.label}
          disabled={lock}
          onKeyDown={(e) => e.stopPropagation()}
          onBlur={(e) => e.target.value.trim() && e.target.value !== set.label && edit('Naziv ladica', (s) => (s.label = e.target.value.trim().slice(0, 200)))}
        />
      </Row>
      <Row label="Broj ladica">
        <IntInput label="Broj ladica" value={set.count} min={1} max={MAX_DRAWERS} disabled={lock} onCommit={(v) => edit('Broj ladica', (s) => (s.count = v))} />
      </Row>
      <Row label="Pregrade">
        <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input
            type="checkbox"
            aria-label="Pregrade između ladica"
            disabled={lock || set.count < 2}
            checked={!!set.dividerThickness}
            onChange={(e) =>
              edit(e.target.checked ? 'Pregrade između ladica' : 'Bez pregrada između ladica', (s) => {
                if (e.target.checked) s.dividerThickness = 18000;
                else delete s.dividerThickness;
              })
            }
          />
          {set.dividerThickness ? (
            <MmInput label="Debljina pregrada" value={set.dividerThickness} min={1} max={50} disabled={lock} onCommit={(v) => edit('Debljina pregrada ladica', (s) => (s.dividerThickness = v))} />
          ) : (
            <span className="faint">između ladica</span>
          )}
        </span>
      </Row>
      <Row label="Klizači (bok)">
        <MmInput label="Prostor za klizače" value={set.slideClearance} min={0} max={100} disabled={lock} onCommit={(v) => edit('Prostor za klizače', (s) => (s.slideClearance = v))} />
      </Row>
      <Row label="Zazor gore">
        <MmInput label="Gornji zazor" value={set.topClearance} min={0} max={200} disabled={lock} onCommit={(v) => edit('Gornji zazor ladica', (s) => (s.topClearance = v))} />
      </Row>
      <Row label="Zazor dolje">
        <MmInput label="Donji zazor" value={set.bottomClearance} min={0} max={200} disabled={lock} onCommit={(v) => edit('Donji zazor ladica', (s) => (s.bottomClearance = v))} />
      </Row>
      <Row label="Zazor od leđa">
        <MmInput label="Zazor od leđa" value={set.rearClearance ?? DEFAULT_DRAWER_REAR_CLEARANCE} min={0} max={200} disabled={lock} onCommit={(v) => edit('Zazor ladica od leđa', (s) => (s.rearClearance = v))} />
      </Row>
      <Row label="Dužina naprijed ±">
        <MmInput label="Pomak prednje ivice ladica (+ duže, − kraće)" value={set.depthFront ?? 0} min={-500} max={500} disabled={lock} onCommit={(v) => edit('Pomak dužine ladica', (s) => (v ? (s.depthFront = v) : delete s.depthFront))} />
      </Row>
      <Row label="Debljina kutije">
        <MmInput label="Debljina kutije" value={set.boxThickness} min={1} max={50} disabled={lock} onCommit={(v) => edit('Debljina kutije ladica', (s) => (s.boxThickness = v))} />
      </Row>
      <Row label="Gradnja">
        <label className="opt-row" title="Bez kvačice: kutija nema prednju stranicu — fronta ladice je zatvara">
          <input type="checkbox" disabled={lock} checked={set.frontPanel !== false} onChange={(e) => edit(e.target.checked ? 'Ladice s prednjom stranicom' : 'Ladice bez prednje stranice (fronta je prednja)', (s) => (e.target.checked ? delete s.frontPanel : (s.frontPanel = false)))} /> sa prednjom stranicom
        </label>
      </Row>
      <Row label="Preklop">
        <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span className="faint">{set.sidesBetween ? 'prolaze: prednja i zadnja' : 'prolaze: bokovi'}</span>
          <button className="btn sm" disabled={lock} title="Zamijeni koje daske prolaze cijelom dužinom: bokovi, ili prednja i zadnja" onClick={() => edit('Zamjena preklopa ladica', (s) => (s.sidesBetween ? delete s.sidesBetween : (s.sidesBetween = true)))}>
            ⇄ Zamijeni preklop
          </button>
        </span>
      </Row>
      <div className="drawer-boards">
        <div className="drawer-boards-head">
          <strong>Daske {index === null ? '(ladica 1 — prikaz)' : `ladice ${index}`}</strong>
          {index !== null && (
            <label className="opt-row" title="Izmjene dasaka važe za sve ladice u setu">
              <input type="checkbox" checked={allDrawers} onChange={(e) => setAllDrawers(e.target.checked)} /> sve ladice
            </label>
          )}
        </div>
        {boards.map((b) => {
          const e = drawerBoardEdit(set, shown, b);
          return (
            <div key={b} className="drawer-board-row">
              <label className="opt-row" title="Bez kvačice: daska se ne pravi">
                <input type="checkbox" disabled={lock} checked={!e.off} onChange={(ev) => editBoard(b, { off: !ev.target.checked }, ev.target.checked ? 'Vrati dasku' : 'Isključi dasku')} /> {DRAWER_BOARD_LABEL[b]}
              </label>
              {b !== 'pod' && (
                <span title="Pomak visine gornje ivice (− niže, + više)" style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <span className="faint">visina ±</span>
                  <MmInput label={`Pomak visine: ${DRAWER_BOARD_LABEL[b]}`} value={e.dh ?? 0} min={-500} max={500} disabled={lock || !!e.off} onCommit={(v) => editBoard(b, { dh: v }, 'Visina daske ladice')} />
                </span>
              )}
            </div>
          );
        })}
      </div>
      <Row label="Dno: materijal">
        <select className="input" disabled={lock} value={set.bottomSlot} onChange={(e) => edit('Materijal dna ladica', (s) => (s.bottomSlot = e.target.value as MaterialSlot))}>
          {(Object.keys(SLOT_LABEL) as MaterialSlot[]).map((k) => (
            <option key={k} value={k}>
              {SLOT_LABEL[k]}
            </option>
          ))}
        </select>
      </Row>
      <Row label="Dno: debljina">
        <MmInput label="Debljina dna" value={set.bottomThickness} min={1} max={50} disabled={lock} onCommit={(v) => edit('Debljina dna ladica', (s) => (s.bottomThickness = v))} />
      </Row>
      <p className="faint" style={{ fontSize: 11, margin: '4px 0' }}>
        Kutija: bokovi, prednja i zadnja, dno odozdo. Dubina: do fronte ladice (kako god je fronta postavljena), bez fronte do najpliće ploče oko prostora; zazor od leđa se oduzima. Dekor materijala se bira u „Podrazumijevani materijali”.
      </p>
      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        <button
          className="btn sm ghost danger"
          disabled={lock}
          onClick={() => {
            exec('Obriši ladice', (d) => {
              const a = d.assemblies.find((x) => x.id === inst.id);
              if (a) a.drawerSets = (a.drawerSets ?? []).filter((s) => s.id !== set.id);
            });
            onRemoved();
          }}
        >
          Obriši ladice
        </button>
      </div>
    </div>
  );
}
